import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { X, Undo2, Redo2, Check, ArrowLeft, Search, ImagePlus, Upload } from 'lucide-react';
import { Textbox, FabricImage as FabricImg } from 'fabric';
import { CanvasWorkspace } from './CanvasWorkspace';
import { Toolbar } from './Toolbar';
import { TextPanel } from './TextPanel';
import { StickerPanel } from './StickerPanel';
import { TemplatePanel } from './TemplatePanel';
import { LayerManager } from './layers/LayerManager';
import { ExportMenu } from './ExportMenu';
import { useCanvasHistory } from './hooks/useCanvasHistory';
import { useCanvasExport } from './hooks/useCanvasExport';
import { ASPECT_RATIOS, GRADIENT_PRESETS, SOLID_COLORS } from './utils/canvas-helpers';
import { applyTemplate } from './utils/apply-template';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import './CanvasEditor.css';

let layerCounter = 0;

/**
 * Main canvas editor.
 * mode="overlay" (default): fullscreen fixed overlay controlled by open/onClose
 * mode="standalone": fixed z-60 overlay, always visible, used by LabPage
 */
export const CanvasEditor = ({
  open,
  onClose,
  onExport,
  isDark,
  mode = 'overlay',
  initialTemplate = null,
  onBack = null,
}) => {
  const fabricRef = useRef(null);
  const fileInputRef = useRef(null);
  const bgSwapRef = useRef(null);
  const [activeTool, setActiveTool] = useState(null);
  const [aspectRatio, setAspectRatio] = useState(ASPECT_RATIOS[0]);
  const [background, setBackground] = useState({ type: 'solid', value: '#FFFFFF' });
  const [selectedObject, setSelectedObject] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [layerUpdateKey, setLayerUpdateKey] = useState(0);

  const { saveState, undo, redo, canUndo, canRedo, clearHistory } = useCanvasHistory(fabricRef);
  const { exportAsBlob } = useCanvasExport(fabricRef);

  // Set default object controls for new objects
  const configureObject = useCallback((obj) => {
    obj.set({
      cornerSize: 12,
      touchCornerSize: 40,
      cornerPadding: 10,
      cornerColor: '#6366f1',
      cornerStrokeColor: '#4f46e5',
      borderColor: '#6366f1',
      transparentCorners: false,
    });
    obj.__id = `layer-${++layerCounter}`;
    return obj;
  }, []);

  // Handle object modifications for undo/redo
  const handleObjectModified = useCallback(() => {
    saveState();
    setIsDirty(true);
    setLayerUpdateKey(k => k + 1);
  }, [saveState]);

  // Handle selection changes — auto-open text panel for text objects
  const handleSelectionChange = useCallback((obj) => {
    setSelectedObject(obj);
    if (obj?.type === 'textbox') {
      setActiveTool('text');
    }
  }, []);

  // Add text to canvas
  const addText = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    const text = new Textbox('Tap to edit', {
      left: canvas.width / 2,
      top: canvas.height / 2,
      originX: 'center',
      originY: 'center',
      fontSize: 48,
      fontFamily: 'Inter, system-ui, sans-serif',
      fill: '#000000',
      textAlign: 'center',
      width: canvas.width * 0.6,
      editable: true,
    });

    configureObject(text);
    text.__layerName = 'Text';
    canvas.add(text);
    canvas.setActiveObject(text);
    canvas.renderAll();
    saveState();
    setIsDirty(true);
  }, [fabricRef, configureObject, saveState]);

  // Add image from file input
  const handleImageUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = fabricRef.current;
        if (!canvas) return;

        const fabricImage = new FabricImg(img);
        // Scale to fit ~60% of canvas width
        const targetWidth = canvas.width * 0.6;
        fabricImage.scaleToWidth(targetWidth);
        fabricImage.set({
          left: canvas.width / 2,
          top: canvas.height / 2,
          originX: 'center',
          originY: 'center',
        });

        configureObject(fabricImage);
        fabricImage.__layerName = 'Image';
        canvas.add(fabricImage);
        canvas.setActiveObject(fabricImage);
        canvas.renderAll();
        saveState();
        setIsDirty(true);
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
    // Reset input
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [fabricRef, configureObject, saveState]);

  // Handle tool selection
  const handleToolSelect = useCallback((tool) => {
    if (tool === 'text') {
      // If no text is selected, add one first
      const canvas = fabricRef.current;
      const active = canvas?.getActiveObject();
      if (!active || active.type !== 'textbox') {
        addText();
      }
      // Open text panel for styling
      setActiveTool('text');
      return;
    }
    if (tool === 'image') {
      fileInputRef.current?.click();
      setActiveTool(null);
      return;
    }
    setActiveTool(tool);
  }, [addText, fabricRef]);

  // Delete selected object
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const canvas = fabricRef.current;
        const active = canvas?.getActiveObject();
        if (active && !active.isEditing && !active.__undeletable) {
          canvas.remove(active);
          canvas.renderAll();
          saveState();
        }
      }
      // Undo: Ctrl/Cmd + Z
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
      // Redo: Ctrl/Cmd + Shift + Z
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        redo();
      }
    };
    if (open || mode === 'standalone') {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, mode, fabricRef, saveState, undo, redo]);

  // Save initial state when canvas is ready
  useEffect(() => {
    if ((open || mode === 'standalone') && fabricRef.current) {
      const timer = setTimeout(() => saveState(), 100);
      return () => clearTimeout(timer);
    }
  }, [open, mode, saveState]);

  // Apply initialTemplate on first mount (standalone mode only)
  const initialTemplateApplied = useRef(false);
  useEffect(() => {
    if (mode !== 'standalone' || !initialTemplate || initialTemplateApplied.current) return;
    const timer = setTimeout(async () => {
      if (!fabricRef.current) return;
      initialTemplateApplied.current = true;
      try {
        await applyTemplate(fabricRef.current, initialTemplate, {
          onAspectChange: setAspectRatio,
          onBackgroundChange: setBackground,
          onModified: () => saveState(),
        });
      } catch (e) {
        console.error('Failed to apply initial template:', e);
      }
    }, 300);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, initialTemplate]);

  // Handle close with dirty check
  const handleClose = useCallback(() => {
    if (isDirty) {
      if (!window.confirm('Discard your canvas? Changes will be lost.')) return;
    }
    setActiveTool(null);
    setIsDirty(false);
    clearHistory();
    onClose();
  }, [isDirty, clearHistory, onClose]);

  // Handle export — action: 'done' (overlay default) | 'post' | 'download' | 'copy'
  const handleExport = useCallback(async (action = 'done') => {
    setExporting(true);
    try {
      const blob = await exportAsBlob();
      const preview = URL.createObjectURL(blob);
      onExport({
        blob,
        preview,
        type: 'image',
        action,
        width: aspectRatio.width,
        height: aspectRatio.height,
      });
      setIsDirty(false);
      clearHistory();
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Export failed. Try again.');
    } finally {
      setExporting(false);
    }
  }, [exportAsBlob, onExport, aspectRatio, clearHistory]);

  // Handle background image upload
  const handleBgImageUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setBackground({ type: 'image', value: url });
    saveState();
    setIsDirty(true);
  }, [saveState]);

  if (mode === 'overlay' && !open) return null;

  const bgClass = isDark ? 'bg-black' : 'bg-white';
  const textClass = isDark ? 'text-white' : 'text-gray-900';
  const mutedClass = isDark ? 'text-white/40' : 'text-gray-400';
  const borderClass = isDark ? 'border-white/10' : 'border-gray-200';
  const panelBg = isDark
    ? (mode === 'standalone' ? 'border-white/5' : 'bg-zinc-900')
    : 'bg-gray-50';

  return (
    <div className={cn(
      mode === 'standalone' ? "canvas-editor-standalone" : "canvas-editor-overlay",
      mode === 'standalone' ? "lab-editor-bg" : (isDark ? "bg-zinc-800" : "bg-gray-200")
    )}>
      {/* Top Bar */}
      {mode === 'standalone' ? (
        /* ── LAB TOPBAR — BLVX branded ── */
        <div className="lab-topbar">
          {/* Left: back + logo + breadcrumb */}
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="lab-topbar-back" title="Back to gallery">
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div className="lab-topbar-divider" />
            <span className="lab-topbar-logo">
              BL<span className="lab-topbar-vx">VX</span>
            </span>
            <div className="lab-topbar-divider" />
            <span className="lab-topbar-breadcrumb">
              <span className="lab-topbar-bc-sep">›</span>
              <span className="lab-topbar-bc-current">The Lab</span>
            </span>

            {/* Format tabs */}
            <div className="lab-format-group">
              {ASPECT_RATIOS.map((ar) => (
                <button
                  key={ar.key}
                  onClick={() => setAspectRatio(ar)}
                  className={cn("lab-format-btn", aspectRatio.key === ar.key && "lab-format-btn--on")}
                >
                  {ar.label}
                </button>
              ))}
            </div>
          </div>

          {/* Right: undo/redo + Drop to Block */}
          <div className="flex items-center gap-2">
            <button onClick={undo} disabled={!canUndo} className={cn("lab-topbar-icon-btn", !canUndo && 'opacity-30')} title="Undo">
              <Undo2 className="h-4 w-4" />
            </button>
            <button onClick={redo} disabled={!canRedo} className={cn("lab-topbar-icon-btn", !canRedo && 'opacity-30')} title="Redo">
              <Redo2 className="h-4 w-4" />
            </button>
            <div className="lab-topbar-divider" />
            <button
              onClick={() => handleExport('post')}
              disabled={exporting}
              className={cn("lab-drop-btn", exporting && 'opacity-60')}
            >
              {exporting ? 'Dropping...' : '+ Drop to Block'}
            </button>
            <ExportMenu
              onPost={() => handleExport('post')}
              onDownload={() => handleExport('download')}
              onCopy={() => handleExport('copy')}
              exporting={exporting}
              isDark={isDark}
              compact={true}
            />
          </div>
        </div>
      ) : (
        /* ── OVERLAY TOPBAR (default) ── */
        <div className={cn("canvas-editor-topbar", borderClass)}>
          <button onClick={handleClose} className={cn("p-2", mutedClass)}>
            <X className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2">
            <button onClick={undo} disabled={!canUndo} className={cn("p-2 transition-opacity", canUndo ? textClass : mutedClass, !canUndo && 'opacity-30')}>
              <Undo2 className="h-5 w-5" />
            </button>
            <button onClick={redo} disabled={!canRedo} className={cn("p-2 transition-opacity", canRedo ? textClass : mutedClass, !canRedo && 'opacity-30')}>
              <Redo2 className="h-5 w-5" />
            </button>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); handleExport(); }}
            disabled={exporting}
            className={cn(
              "flex items-center gap-1.5 px-4 py-1.5 text-sm font-display tracking-wider",
              isDark ? 'bg-white text-black' : 'bg-black text-white',
              exporting && 'opacity-50'
            )}
          >
            <Check className="h-4 w-4" />
            {exporting ? 'Exporting...' : 'Done'}
          </button>
        </div>
      )}

      {/* Canvas */}
      <CanvasWorkspace
        fabricRef={fabricRef}
        canvasWidth={aspectRatio.width}
        canvasHeight={aspectRatio.height}
        background={background}
        onObjectModified={handleObjectModified}
        onSelectionChange={handleSelectionChange}
        formatLabel={`${aspectRatio.label} \u2014 ${aspectRatio.width} \u00d7 ${aspectRatio.height}`}
      />

      {/* Swap Photo — contextual shortcut when a photo template is active */}
      {background.type === 'image' && activeTool !== 'background' && (
        <div className="flex justify-center gap-2 py-1.5">
          <button
            onClick={() => bgSwapRef.current?.click()}
            className={cn(
              "flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors",
              isDark
                ? 'bg-white/10 border-white/20 text-white/70 hover:bg-white/20 hover:text-white'
                : 'bg-black/5 border-black/15 text-black/60 hover:bg-black/10 hover:text-black'
            )}
          >
            <Upload className="h-3.5 w-3.5" />
            Your Photo
          </button>
          <button
            onClick={() => setActiveTool('background')}
            className={cn(
              "flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors",
              isDark
                ? 'bg-white/10 border-white/20 text-white/70 hover:bg-white/20 hover:text-white'
                : 'bg-black/5 border-black/15 text-black/60 hover:bg-black/10 hover:text-black'
            )}
          >
            <ImagePlus className="h-3.5 w-3.5" />
            Stock Photos
          </button>
        </div>
      )}

      {/* Slide-up Panel */}
      {activeTool && (
        <div
          className={cn("canvas-editor-panel", panelBg, borderClass)}
          style={isDark && mode === 'standalone' ? { background: 'var(--surface, #151A24)' } : undefined}
        >
          {activeTool === 'background' && (
            <BackgroundPanel
              background={background}
              onSelect={setBackground}
              onBgImage={handleBgImageUpload}
              isDark={isDark}
            />
          )}
          {activeTool === 'layers' && (
            <LayerManager
              fabricRef={fabricRef}
              isDark={isDark}
              onUpdate={layerUpdateKey}
            />
          )}
          {activeTool === 'text' && (
            <TextPanel
              fabricRef={fabricRef}
              selectedObject={selectedObject}
              onModified={handleObjectModified}
              isDark={isDark}
            />
          )}
          {activeTool === 'sticker' && (
            <StickerPanel
              fabricRef={fabricRef}
              onModified={handleObjectModified}
              isDark={isDark}
            />
          )}
          {activeTool === 'template' && (
            <TemplatePanel
              fabricRef={fabricRef}
              onModified={handleObjectModified}
              onBackgroundChange={setBackground}
              onAspectChange={setAspectRatio}
              isDark={isDark}
            />
          )}
        </div>
      )}

      {/* Aspect Ratio Selector — only shown in overlay mode (standalone uses topbar format tabs) */}
      {mode !== 'standalone' && (
        <div className={cn("canvas-editor-aspect-bar", borderClass)}>
          {ASPECT_RATIOS.map((ar) => (
            <button
              key={ar.key}
              onClick={() => setAspectRatio(ar)}
              className={cn(
                "px-3 py-1.5 text-[11px] font-display tracking-wider uppercase rounded-sm transition-colors",
                aspectRatio.key === ar.key
                  ? isDark ? 'bg-white/15 text-white' : 'bg-gray-900/10 text-gray-900'
                  : mutedClass
              )}
            >
              {ar.label}
            </button>
          ))}
        </div>
      )}

      {/* Bottom Toolbar */}
      <Toolbar
        activeTool={activeTool}
        onToolSelect={handleToolSelect}
        isDark={isDark}
      />

      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        className="hidden"
      />
      <input
        ref={bgSwapRef}
        type="file"
        accept="image/*"
        onChange={handleBgImageUpload}
        className="hidden"
      />
    </div>
  );
};

// Stock photo categories with curated Unsplash images featuring diverse representation
const STOCK_PHOTO_CATEGORIES = [
  { key: 'people', label: 'People' },
  { key: 'city', label: 'City' },
  { key: 'nature', label: 'Nature' },
  { key: 'abstract', label: 'Abstract' },
  { key: 'texture', label: 'Texture' },
];

const STOCK_PHOTOS = [
  // People — centered on Black and brown representation
  { id: 'p1',  cat: 'people', url: 'https://images.unsplash.com/photo-1632765854612-9b02b6ec2b15?w=800&h=800&fit=crop&auto=format&q=80', alt: 'Woman with afro' },
  { id: 'p2',  cat: 'people', url: 'https://images.unsplash.com/photo-1570158268183-d296b2892211?w=800&h=800&fit=crop&auto=format&q=80', alt: 'Man portrait' },
  { id: 'p3',  cat: 'people', url: 'https://images.unsplash.com/photo-1530785602389-07594beb8b73?w=800&h=800&fit=crop&auto=format&q=80', alt: 'Smiling woman with turban' },
  { id: 'p4',  cat: 'people', url: 'https://images.unsplash.com/photo-1527203561188-dae1bc1a417f?w=800&h=800&fit=crop&auto=format&q=80', alt: 'Woman with braids' },
  { id: 'p5',  cat: 'people', url: 'https://images.unsplash.com/photo-1522529599102-193c0d76b5b6?w=800&h=800&fit=crop&auto=format&q=80', alt: 'Man smiling' },
  { id: 'p6',  cat: 'people', url: 'https://images.unsplash.com/photo-1587064712555-6e206484699b?w=800&h=800&fit=crop&auto=format&q=80', alt: 'Man in crew neck' },
  { id: 'p7',  cat: 'people', url: 'https://images.unsplash.com/photo-1765607476292-886bc1e648a1?w=800&h=800&fit=crop&auto=format&q=80', alt: 'Young woman portrait' },
  { id: 'p8',  cat: 'people', url: 'https://images.unsplash.com/photo-1565884280295-98eb83e41c65?w=800&h=800&fit=crop&auto=format&q=80', alt: 'Man in red shirt' },
  { id: 'p9',  cat: 'people', url: 'https://images.unsplash.com/photo-1765607476376-9574ea76b2ee?w=800&h=800&fit=crop&auto=format&q=80', alt: 'Young Black woman thoughtful' },
  { id: 'p10', cat: 'people', url: 'https://images.unsplash.com/photo-1519699047748-de8e457a634e?w=800&h=800&fit=crop&auto=format&q=80', alt: 'Woman with natural hair' },
  // Latino/Hispanic representation
  { id: 'p11', cat: 'people', url: 'https://images.unsplash.com/photo-1648757766966-43d24bf7a264?w=800&h=800&fit=crop&auto=format&q=80', alt: 'Hispanic man in suit' },
  { id: 'p12', cat: 'people', url: 'https://images.unsplash.com/photo-1679508004557-52bfaec15fe1?w=800&h=800&fit=crop&auto=format&q=80', alt: 'Colombian woman portrait' },
  { id: 'p13', cat: 'people', url: 'https://images.unsplash.com/photo-1727702079139-c5e78976dab5?w=800&h=800&fit=crop&auto=format&q=80', alt: 'Hispanic man studio portrait' },
  { id: 'p14', cat: 'people', url: 'https://images.unsplash.com/photo-1739825353871-2b9c6716142a?w=800&h=800&fit=crop&auto=format&q=80', alt: 'Latina woman curly hair' },
  // Asian representation
  { id: 'p15', cat: 'people', url: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=800&h=800&fit=crop&auto=format&q=80', alt: 'Asian woman portrait' },
  // City
  { id: 'c1', cat: 'city', url: 'https://images.unsplash.com/photo-1514565131-fce0801e5785?w=800&h=800&fit=crop&auto=format&q=80', alt: 'NYC skyline night' },
  { id: 'c2', cat: 'city', url: 'https://images.unsplash.com/photo-1519501025264-65ba15a82390?w=800&h=800&fit=crop&auto=format&q=80', alt: 'City streets neon' },
  { id: 'c3', cat: 'city', url: 'https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=800&h=800&fit=crop&auto=format&q=80', alt: 'Urban skyline sunset' },
  { id: 'c4', cat: 'city', url: 'https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=800&h=800&fit=crop&auto=format&q=80', alt: 'City buildings aerial' },
  { id: 'c5', cat: 'city', url: 'https://images.unsplash.com/photo-1517935706615-2717063c2225?w=800&h=800&fit=crop&auto=format&q=80', alt: 'Street at night' },
  // Nature
  { id: 'n1', cat: 'nature', url: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&h=800&fit=crop&auto=format&q=80', alt: 'Tropical beach' },
  { id: 'n2', cat: 'nature', url: 'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=800&h=800&fit=crop&auto=format&q=80', alt: 'Misty forest' },
  { id: 'n3', cat: 'nature', url: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=800&h=800&fit=crop&auto=format&q=80', alt: 'Mountain sunrise' },
  { id: 'n4', cat: 'nature', url: 'https://images.unsplash.com/photo-1518173946687-a1e0e2a9e4b5?w=800&h=800&fit=crop&auto=format&q=80', alt: 'Palm trees golden hour' },
  { id: 'n5', cat: 'nature', url: 'https://images.unsplash.com/photo-1431440869543-efaf3388c585?w=800&h=800&fit=crop&auto=format&q=80', alt: 'Ocean waves' },
  // Abstract
  { id: 'a1', cat: 'abstract', url: 'https://images.unsplash.com/photo-1557672172-298e090bd0f1?w=800&h=800&fit=crop&auto=format&q=80', alt: 'Pink fluid abstract' },
  { id: 'a2', cat: 'abstract', url: 'https://images.unsplash.com/photo-1550684376-efcbd6e3f031?w=800&h=800&fit=crop&auto=format&q=80', alt: 'Neon light abstract' },
  { id: 'a3', cat: 'abstract', url: 'https://images.unsplash.com/photo-1567095761054-7a02e69e5b2b?w=800&h=800&fit=crop&auto=format&q=80', alt: 'Gradient abstract' },
  { id: 'a4', cat: 'abstract', url: 'https://images.unsplash.com/photo-1579546929518-9e396f3cc809?w=800&h=800&fit=crop&auto=format&q=80', alt: 'Colorful gradient' },
  { id: 'a5', cat: 'abstract', url: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=800&h=800&fit=crop&auto=format&q=80', alt: 'Purple swirl abstract' },
  // Texture
  { id: 't1', cat: 'texture', url: 'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=800&h=800&fit=crop&auto=format&q=80', alt: 'Marble texture' },
  { id: 't2', cat: 'texture', url: 'https://images.unsplash.com/photo-1553356084-58ef4a67b2a7?w=800&h=800&fit=crop&auto=format&q=80', alt: 'Paint strokes' },
  { id: 't3', cat: 'texture', url: 'https://images.unsplash.com/photo-1528459801416-a9e53bbf4e17?w=800&h=800&fit=crop&auto=format&q=80', alt: 'Paper texture' },
  { id: 't4', cat: 'texture', url: 'https://images.unsplash.com/photo-1517999144091-3d9dca6d1e43?w=800&h=800&fit=crop&auto=format&q=80', alt: 'Dark concrete' },
  { id: 't5', cat: 'texture', url: 'https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?w=800&h=800&fit=crop&auto=format&q=80', alt: 'Gold foil texture' },
];

/**
 * Background picker sub-panel with stock photos.
 */
const BackgroundPanel = ({ background, onSelect, onBgImage, isDark }) => {
  const mutedClass = isDark ? 'text-white/40' : 'text-gray-400';
  const textClass = isDark ? 'text-white' : 'text-gray-900';
  const bgInputRef = useRef(null);
  const [photoCat, setPhotoCat] = useState('people');
  const [photoSearch, setPhotoSearch] = useState('');

  const filteredPhotos = useMemo(() => {
    let photos = STOCK_PHOTOS.filter(p => p.cat === photoCat);
    if (photoSearch.trim()) {
      const q = photoSearch.toLowerCase();
      photos = STOCK_PHOTOS.filter(p => p.alt.toLowerCase().includes(q));
    }
    return photos;
  }, [photoCat, photoSearch]);

  return (
    <div className="p-3 space-y-3 max-h-[60vh] overflow-y-auto hide-scrollbar">
      {/* Stock Photos */}
      <div>
        <p className={cn("text-[10px] font-display tracking-widest uppercase mb-2", mutedClass)}>
          Stock Photos
        </p>
        {/* Search */}
        <div className={cn("flex items-center gap-1.5 px-2 py-1.5 rounded-lg border mb-2", isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-white')}>
          <Search className={cn("h-3.5 w-3.5 shrink-0", mutedClass)} />
          <input
            type="text"
            placeholder="Search photos..."
            value={photoSearch}
            onChange={(e) => setPhotoSearch(e.target.value)}
            className={cn("flex-1 bg-transparent text-xs outline-none placeholder:text-current/30", textClass)}
          />
        </div>
        {/* Category pills */}
        {!photoSearch && (
          <div className="flex gap-1.5 mb-2 overflow-x-auto hide-scrollbar">
            {STOCK_PHOTO_CATEGORIES.map(c => (
              <button
                key={c.key}
                onClick={() => setPhotoCat(c.key)}
                className={cn(
                  "px-2.5 py-1 text-[10px] font-display tracking-wider uppercase whitespace-nowrap rounded-full transition-colors shrink-0",
                  photoCat === c.key
                    ? isDark ? 'bg-white text-black' : 'bg-gray-900 text-white'
                    : isDark ? 'text-white/40 hover:text-white' : 'text-gray-400 hover:text-gray-900'
                )}
              >
                {c.label}
              </button>
            ))}
          </div>
        )}
        {/* Photo grid */}
        <div className="grid grid-cols-4 gap-1.5">
          {filteredPhotos.map(photo => (
            <button
              key={photo.id}
              onClick={() => onSelect({ type: 'image', value: photo.url })}
              className={cn(
                "aspect-square rounded-lg overflow-hidden border transition-all hover:scale-105 active:scale-95",
                background.type === 'image' && background.value === photo.url
                  ? 'border-amber-500 ring-1 ring-amber-500'
                  : isDark ? 'border-white/10' : 'border-gray-200'
              )}
            >
              <img src={photo.url} alt={photo.alt} className="w-full h-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
        {filteredPhotos.length === 0 && (
          <p className={cn("text-xs text-center py-3", mutedClass)}>No photos found</p>
        )}
      </div>

      {/* Solid colors */}
      <div>
        <p className={cn("text-[10px] font-display tracking-widest uppercase mb-2", mutedClass)}>
          Solid
        </p>
        <div className="flex flex-wrap gap-2">
          {SOLID_COLORS.map((color) => (
            <button
              key={color}
              onClick={() => onSelect({ type: 'solid', value: color })}
              className={cn(
                "w-8 h-8 rounded-sm border transition-transform hover:scale-110",
                background.type === 'solid' && background.value === color
                  ? 'border-amber-500 ring-1 ring-amber-500'
                  : isDark ? 'border-white/20' : 'border-gray-200'
              )}
              style={{ backgroundColor: color }}
            />
          ))}
        </div>
      </div>

      {/* Gradients */}
      <div>
        <p className={cn("text-[10px] font-display tracking-widest uppercase mb-2", mutedClass)}>
          Gradient
        </p>
        <div className="flex flex-wrap gap-2">
          {GRADIENT_PRESETS.map((g) => (
            <button
              key={g.key}
              onClick={() => onSelect({ type: 'gradient', value: g.css })}
              className={cn(
                "w-8 h-8 rounded-sm border transition-transform hover:scale-110",
                background.type === 'gradient' && background.value === g.css
                  ? 'border-amber-500 ring-1 ring-amber-500'
                  : isDark ? 'border-white/20' : 'border-gray-200'
              )}
              style={{ background: g.css }}
            />
          ))}
        </div>
      </div>

      {/* Image upload */}
      <div>
        <p className={cn("text-[10px] font-display tracking-widest uppercase mb-2", mutedClass)}>
          Upload
        </p>
        <button
          onClick={() => bgInputRef.current?.click()}
          className={cn(
            "px-3 py-1.5 text-xs border rounded-lg transition-colors",
            isDark ? 'border-white/20 text-white/60 hover:text-white' : 'border-gray-200 text-gray-400 hover:text-gray-900'
          )}
        >
          Upload Background
        </button>
        <input
          ref={bgInputRef}
          type="file"
          accept="image/*"
          onChange={onBgImage}
          className="hidden"
        />
      </div>
    </div>
  );
};
